/**
 * Central Dashboard widget registry.
 *
 * This module is the single source of truth for the dashboard widget contract
 * shared by the worker (backend authorization + data) and the web app (display
 * registry). It is deliberately pure TypeScript with NO runtime dependency on
 * D1, Hono, React or any other package so it can be imported from both apps
 * without bundling surprises.
 *
 * Rules enforced around this registry:
 *  - The worker returns ONLY widgets the authenticated user is authorized to
 *    receive. ORG_ADMIN is a deliberate tenant-admin bypass that receives the
 *    curated org-overview summary. Everyone else is resolved through the
 *    non-admin profile model (resolveNonAdminProfile) which derives the
 *    dashboard content purely from effective permission keys - never from role
 *    display names or project assignments. All non-admin widgets are scoped to
 *    the caller's entire organization.
 *  - The web renders ONLY widgets present in the `/dashboard/stats` response
 *    and uses the `profile` contract to lay them out (cards / queue / recent).
 *  - Every widget key here MUST have a matching backend builder in
 *    apps/worker/src/routes/dashboard.ts and a matching display renderer in
 *    apps/web/src/components/dashboard/registry.tsx (covered by tests).
 */

export type DashboardWidgetScope = "org" | "project" | "owned";

export type DashboardWidgetCategory =
  | "summary"
  | "analytics"
  | "author"
  | "manager"
  | "cluster"
  | "readonly"
  | "queue"
  | "recent";

/**
 * Single source of truth for the labour workflow statuses. The worker stores
 * exactly these status values and the web renders exactly these labels, so the
 * mapping cannot drift between apps. Never rename an existing key.
 */
export const REQUIREMENT_STATUS_KEYS = [
  "DRAFT",
  "PENDING_MANAGER_APPROVAL",
  "MANAGER_APPROVED",
  "REJECTED_BY_MANAGER",
  "APPROVED",
  "REJECTED_BY_CLUSTER_HEAD",
] as const;

/**
 * The single stored status value that represents the FINAL approval state for
 * both labour requirements and actual labour records. Every dashboard card,
 * report, chart and CSV export must use this one definition so the worker and
 * web can never disagree about "approved".
 */
export const FINAL_APPROVED_STATUS = "APPROVED";

/**
 * Normalize any status string to a boolean "is finally approved" answer.
 * Handles the current canonical value and legacy spellings that existed before
 * the workflow was normalized (see migration 0007). Unknown values are never
 * treated as approved.
 */
export function isFinalApproved(status: string | null | undefined): boolean {
  if (!status) return false;
  switch (status.trim().toUpperCase()) {
    case "APPROVED":
    case "FINAL_APPROVED":
    case "CLUSTER_HEAD_APPROVED":
    case "CLUSTER APPROVED":
      return true;
    default:
      return false;
  }
}

/** Readable labels for every stored workflow status (central mapping). */
export const STATUS_LABELS: Record<string, string> = {
  DRAFT: "Draft",
  PENDING_MANAGER_APPROVAL: "Pending Manager Approval",
  MANAGER_APPROVED: "Manager Approved",
  REJECTED_BY_MANAGER: "Rejected by Manager",
  APPROVED: "Approved",
  REJECTED_BY_CLUSTER_HEAD: "Rejected by Cluster Head",
};

/** Readable labels for stored labour classification (used by analysis). */
export const LABOUR_TYPE_LABELS: Record<string, string> = {
  REGULAR: "Regular",
  ADHOC: "Adhoc",
  UNCLASSIFIED: "Unclassified",
};

/** Readable labels for stored skill-level classification (used by analysis). */
export const SKILL_LEVEL_LABELS: Record<string, string> = {
  SKILLED: "Skilled",
  SEMI_SKILLED: "Semi-skilled",
  UNSKILLED: "Unskilled",
  UNCLASSIFIED: "Unclassified",
};

export interface DashboardWidgetDefinition {
  /** Unique, stable widget key. Never rename; clients cache by it. */
  key: string;
  /** Human-readable title shown on the card. */
  label: string;
  /** Data scope: org-wide (admins), organization-wide permitted records, owned (created_by). */
  scope: DashboardWidgetScope;
  /** Permission keys that grant access (ANY-of semantics). */
  permissions: string[];
  category: DashboardWidgetCategory;
  /** Text shown when the widget has no data in range. */
  emptyText: string;
  /** Whether the widget contains a user action (vs. pure read). */
  hasAction: boolean;
  /**
   * Organization-Analysis / org-overview widget: never resolved for non-admin
   * users regardless of their permission grants. Analysis widgets live in the
   * admin-only /dashboard/analysis response; org summary cards live in the
   * curated admin /dashboard/stats response. Non-admin content is instead
   * resolved through resolveNonAdminProfile.
   */
  adminOnly?: boolean;
}

export const APPROVAL_PERMISSION_KEYS = [
  "requirement:manager_approve",
  "requirement:manager_reject",
  "requirement:cluster_approve",
  "requirement:cluster_reject",
  "actual:manager_approve",
  "actual:manager_reject",
  "actual:cluster_approve",
  "actual:cluster_reject",
] as const;

const APPROVAL_ANY = [...APPROVAL_PERMISSION_KEYS];

/**
 * Every dashboard widget, in stable render order. The admin-only summary and
 * analytics set first, then the non-admin permission-derived cards (author /
 * manager / cluster / read-only), then the queue and recent sections. Admin
 * widgets are guarded by `adminOnly`; non-admin content is resolved by
 * capability, not role name.
 */
export const DASHBOARD_WIDGETS: readonly DashboardWidgetDefinition[] = [
  // ── Summary (ORG_ADMIN overview + shared read-only cards) ───────────────
  { key: "projects_total", label: "Total Projects", scope: "org", permissions: ["projects:view"], category: "summary", emptyText: "No projects in this organization.", hasAction: false, adminOnly: true },
  { key: "projects_active", label: "Active Projects", scope: "org", permissions: ["projects:view"], category: "summary", emptyText: "No active projects.", hasAction: false, adminOnly: true },
  { key: "requirements_total", label: "Total Requirements", scope: "project", permissions: ["requirement:view"], category: "summary", emptyText: "No requirements in this range.", hasAction: false, adminOnly: true },
  { key: "actuals_total", label: "Total Actual Labour Entries", scope: "project", permissions: ["actual:view"], category: "summary", emptyText: "No actual labour entries.", hasAction: false, adminOnly: true },
  { key: "requirements_approved", label: "Approved Requirements", scope: "project", permissions: ["requirement:view"], category: "readonly", emptyText: "No approved requirements in this range.", hasAction: false },
  { key: "actuals_approved", label: "Approved Actual Labour", scope: "project", permissions: ["actual:view"], category: "readonly", emptyText: "No approved actual labour in this range.", hasAction: false },
  { key: "actuals_approved_cost", label: "Approved Actual Labour Cost", scope: "project", permissions: ["reports:view"], category: "summary", emptyText: "No approved actual labour cost in this range.", hasAction: false, adminOnly: true },
  { key: "users_active", label: "Active Users", scope: "org", permissions: ["users:view"], category: "summary", emptyText: "No active users.", hasAction: false, adminOnly: true },

  // ── Analytics (Organization Analysis, ORG_ADMIN only) ───────────────────
  { key: "requirements_by_status", label: "Requirements by Status", scope: "project", permissions: ["requirement:view"], category: "analytics", emptyText: "No requirements in this range.", hasAction: false, adminOnly: true },
  { key: "planned_vs_actual", label: "Planned vs Actual Labour", scope: "project", permissions: ["reports:view"], category: "analytics", emptyText: "No planned or actual labour in this range.", hasAction: false, adminOnly: true },
  { key: "regular_vs_adhoc", label: "Labour by Type", scope: "project", permissions: ["reports:view"], category: "analytics", emptyText: "No actual labour in this range.", hasAction: false, adminOnly: true },
  { key: "labour_by_skill", label: "Labour by Skill Level", scope: "project", permissions: ["reports:view"], category: "analytics", emptyText: "No labour data in this range.", hasAction: false, adminOnly: true },
  { key: "cost_by_project", label: "Cost by Project", scope: "project", permissions: ["reports:view"], category: "analytics", emptyText: "No approved actual labour cost in this range.", hasAction: false, adminOnly: true },

  // ── Author: requirements (created_by current user) ─────────────────────
  { key: "my_draft_requirements", label: "Requirement Drafts", scope: "owned", permissions: ["requirement:create"], category: "author", emptyText: "No draft requirements.", hasAction: false },
  { key: "my_action_required_requirements", label: "Requirements Requiring Action", scope: "owned", permissions: ["requirement:create"], category: "author", emptyText: "No returned requirements.", hasAction: false },

  // ── Author: actual labour (created_by current user) ────────────────────
  { key: "my_draft_actuals", label: "Actual Labour Drafts", scope: "owned", permissions: ["actual:create"], category: "author", emptyText: "No draft actuals.", hasAction: false },
  { key: "my_action_required_actuals", label: "Actual Labour Requiring Action", scope: "owned", permissions: ["actual:create"], category: "author", emptyText: "No returned actuals.", hasAction: false },

  // ── Site Engineer six-card status summary (created_by current user) ──────
  // The pure-creator profile resolves these six cards laid out flat (no queue,
  // no recent section). The two "Total" cards are distinct owner-scoped counts;
  // the four combined cards each sum Requirements + Actual Labour at a single
  // current status. Every key has a matching metric
  // (packages/shared/src/dashboard-metrics.ts) so the worker's count builders
  // and the /requirements /actuals list routes share the exact same row set.
  { key: "site_total_requirements", label: "Total Requirements Raised", scope: "owned", permissions: ["requirement:create"], category: "summary", emptyText: "No requirements raised.", hasAction: false },
  { key: "site_total_actuals", label: "Total Actual Labour Raised", scope: "owned", permissions: ["actual:create"], category: "summary", emptyText: "No actual labour raised.", hasAction: false },
  { key: "site_pending_pm", label: "Pending PM Approval", scope: "owned", permissions: ["requirement:create", "actual:create"], category: "summary", emptyText: "No records pending with the project manager.", hasAction: false },
  { key: "site_pending_ch", label: "Pending CH Approval", scope: "owned", permissions: ["requirement:create", "actual:create"], category: "summary", emptyText: "No records pending with the cluster head.", hasAction: false },
  { key: "site_rejected_pm", label: "Rejected by PM", scope: "owned", permissions: ["requirement:create", "actual:create"], category: "summary", emptyText: "No records rejected by the project manager.", hasAction: false },
  { key: "site_rejected_ch", label: "Rejected by CH", scope: "owned", permissions: ["requirement:create", "actual:create"], category: "summary", emptyText: "No records rejected by the cluster head.", hasAction: false },

  // ── Manager approval (four-card dashboard) ─────────────────────────────
  // Project Manager dashboards resolve EXACTLY these four cards laid out flat:
  // the two pending cards plus the combined "by Me" decision cards. There is
  // no separate approval-queue panel and no recent-decisions section anymore.
  { key: "awaiting_manager_requirements", label: "Requirements Pending My Approval", scope: "project", permissions: ["requirement:manager_approve"], category: "manager", emptyText: "No requirements awaiting your approval.", hasAction: true },
  { key: "awaiting_manager_actuals", label: "Actual Labour Pending My Approval", scope: "project", permissions: ["actual:manager_approve"], category: "manager", emptyText: "No actuals awaiting your approval.", hasAction: true },
  { key: "manager_decisions_rejected_by_me", label: "Rejected by Me", scope: "project", permissions: APPROVAL_ANY, category: "manager", emptyText: "No records rejected by you.", hasAction: false },
  { key: "manager_decisions_approved_by_me", label: "Approved by Me", scope: "project", permissions: APPROVAL_ANY, category: "manager", emptyText: "No records approved by you.", hasAction: false },

  // ── Cluster head / final approval (four-card dashboard) ────────────────
  { key: "awaiting_cluster_requirements", label: "Requirements Pending Final Approval", scope: "project", permissions: ["requirement:cluster_approve"], category: "cluster", emptyText: "No requirements awaiting final approval.", hasAction: true },
  { key: "awaiting_cluster_actuals", label: "Actual Labour Pending Final Approval", scope: "project", permissions: ["actual:cluster_approve"], category: "cluster", emptyText: "No actuals awaiting final approval.", hasAction: true },
  { key: "cluster_decisions_rejected_by_me", label: "Rejected by Me", scope: "project", permissions: APPROVAL_ANY, category: "cluster", emptyText: "No records rejected by you.", hasAction: false },
  { key: "cluster_decisions_approved_by_me", label: "Finally Approved by Me", scope: "project", permissions: APPROVAL_ANY, category: "cluster", emptyText: "No records finally approved by you.", hasAction: false },

  // ── Read-only recent section (Viewer / read-only custom roles) ──────────
  { key: "recently_approved_records", label: "Recently Approved Records", scope: "project", permissions: ["requirement:view", "actual:view"], category: "recent", emptyText: "No recently approved records.", hasAction: false },

  // ── Read-only approved records (ORG_ADMIN Organization Analysis) ────────
  { key: "recent_approved_requirements", label: "Recently Approved Requirements", scope: "project", permissions: ["requirement:view"], category: "readonly", emptyText: "No recently approved requirements.", hasAction: false, adminOnly: true },
  { key: "recent_approved_actuals", label: "Recently Approved Actuals", scope: "project", permissions: ["actual:view"], category: "readonly", emptyText: "No recently approved actuals.", hasAction: false, adminOnly: true },
];

export const DASHBOARD_WIDGET_BY_KEY: Readonly<Record<string, DashboardWidgetDefinition>> =
  Object.fromEntries(DASHBOARD_WIDGETS.map((w) => [w.key, w])) as Record<string, DashboardWidgetDefinition>;

/** Widget keys that describe approval queues (rendered with actions). */
export const APPROVAL_QUEUE_KEYS = [
  "awaiting_manager_requirements",
  "awaiting_manager_actuals",
  "awaiting_cluster_requirements",
  "awaiting_cluster_actuals",
] as const;

/** Widget keys that are author-scoped ("My ...") counters/lists. */
export const OWNED_WIDGET_KEYS = DASHBOARD_WIDGETS
  .filter((w) => w.scope === "owned")
  .map((w) => w.key);

export interface DashboardActionDefinition {
  key: string;
  label: string;
  /** The single permission key that gates this action. */
  permission: string;
}

export const DASHBOARD_ACTIONS: readonly DashboardActionDefinition[] = [
  { key: "new_requirement", label: "New Requirement", permission: "requirement:create" },
  { key: "new_actual", label: "New Actual Labour", permission: "actual:create" },
];

// ── Non-admin profile model ────────────────────────────────────────────────
// The non-admin dashboard is one reusable layout (header, actions, <=4 cards,
// one queue, one compact recent) whose content is derived purely from the
// user's effective permissions. Role display names never influence this model,
// so any future custom role works without a new dashboard page.

export interface DashboardNonAdminSection {
  key: string;
  title: string;
}

export interface DashboardNonAdminProfile {
  /**
   * Ordered count-card keys:
   *  - Site Engineer: exactly six owner-scoped status cards
   *  - Manager / Cluster Head (and custom roles with equivalent approval
   *    permissions): exactly four approval cards
   *  - read-only roles: informational approved summaries
   */
  cards: string[];
  /**
   * Primary work queue section. ALWAYS null today: the approval-queue and
   * work-requiring-attention panels were removed, so every non-admin profile
   * is a flat card layout (the field is kept for the layout contract only).
   */
  queue: DashboardNonAdminSection | null;
  /** Compact recent-activity section (one, only when useful). */
  recent: DashboardNonAdminSection | null;
}

const hasPerm = (permissions: string[], key: string) => permissions.includes(key);

/**
 * Resolve the non-admin dashboard profile from effective permission keys.
 *
 *  - Pure creator (Site Engineer): exactly six owner-scoped cards (two distinct
 *    totals + four combined status cards) laid out flat.
 *  - Approval profiles (Project Manager, Cluster Head, or any custom role with
 *    equivalent approval permission keys): exactly four cards laid out flat -
 *    the two pending cards first, then the combined "Rejected by Me" and
 *    "Approved by Me"/"Finally Approved by Me" decision cards. There is NO
 *    queue and NO recent section for these profiles.
 *  - Read-only profiles: informational approved summaries plus one compact
 *    recently-approved section.
 * Role display names never influence this model, so any future custom role
 * with approval permissions automatically receives the matching four-card
 * dashboard.
 */
export function resolveNonAdminProfile(permissions: string[]): DashboardNonAdminProfile {
  const p = permissions ?? [];
  const canReqManager = hasPerm(p, "requirement:manager_approve") || hasPerm(p, "requirement:manager_reject");
  const canActManager = hasPerm(p, "actual:manager_approve") || hasPerm(p, "actual:manager_reject");
  const canReqCluster = hasPerm(p, "requirement:cluster_approve") || hasPerm(p, "requirement:cluster_reject");
  const canActCluster = hasPerm(p, "actual:cluster_approve") || hasPerm(p, "actual:cluster_reject");
  const canCreateReq = hasPerm(p, "requirement:create");
  const canCreateAct = hasPerm(p, "actual:create");
  const canViewReq = hasPerm(p, "requirement:view");
  const canViewAct = hasPerm(p, "actual:view");

  const hasApproval = canReqManager || canActManager || canReqCluster || canActCluster;
  const hasCreate = canCreateReq || canCreateAct;
  const isReadOnly = !hasApproval && !hasCreate;

  if (isReadOnly) {
    // Read-only: informational totals only (no actions), organization-wide,
    // plus one compact recently-approved section.
    const cards: string[] = [];
    if (canViewReq) cards.push("requirements_approved");
    if (canViewAct) cards.push("actuals_approved");
    return {
      cards,
      queue: null,
      recent: canViewReq || canViewAct ? { key: "recently_approved_records", title: "Recently Approved Records" } : null,
    };
  }

  if (!hasApproval) {
    // Pure creator profile (Site Engineer): exactly six owner-scoped cards laid
    // out flat with NO queue or recent section. The order mirrors the workflow
    // narrative: distinct totals first, then the combined pending / rejected
    // status cards. The four combined cards each sum Requirements + Actual
    // Labour at the same current status; the totals intentionally overlap them.
    return {
      cards: [
        "site_total_requirements",
        "site_total_actuals",
        "site_pending_pm",
        "site_pending_ch",
        "site_rejected_pm",
        "site_rejected_ch",
      ],
      queue: null,
      recent: null,
    };
  }

  // Approval profile (Manager / Cluster Head / equivalent custom role): exactly
  // four cards laid out flat. Pending cards first, then the two combined
  // decision cards. No queue, no recent section. A hybrid that holds both
  // manager and cluster grants caps at four cards, prioritizing pending work.
  const cards: string[] = [];
  if (canReqManager) cards.push("awaiting_manager_requirements");
  if (canActManager) cards.push("awaiting_manager_actuals");
  if (canReqCluster) cards.push("awaiting_cluster_requirements");
  if (canActCluster) cards.push("awaiting_cluster_actuals");
  if (canReqManager || canActManager) cards.push("manager_decisions_rejected_by_me", "manager_decisions_approved_by_me");
  if (canReqCluster || canActCluster) cards.push("cluster_decisions_rejected_by_me", "cluster_decisions_approved_by_me");

  return { cards: cards.slice(0, 4), queue: null, recent: null };
}

// ── Wire payloads ──────────────────────────────────────────────────────────
// These describe exactly what the worker returns and the web renders.

export interface DashboardWidgetPayload {
  key: string;
  /** Widget-specific data. Shape is documented per key in the worker builders. */
  data: unknown;
}

export interface DashboardActionPayload {
  key: string;
  label: string;
}

export interface DashboardScopePayload {
  /** org_admin = deliberate tenant-admin bypass; scoped = permission-driven. */
  dashboardType: "org_admin" | "scoped";
  organizationId: string;
  /** Always "all": every non-admin widget is scoped to the caller's organization. */
  projectScope: "all";
}

export interface DashboardProjectFilterPayload {
  projectId: string | null;
  projectsTotal: number;
  projects: { id: string; name: string; code: string }[];
}

export interface DashboardStatsResponse {
  scope: DashboardScopePayload;
  /** Quick actions derived from the caller's create permissions (admin: none). */
  actions: DashboardActionPayload[];
  /** Only widgets the caller is authorized to receive, in render order. */
  widgets: DashboardWidgetPayload[];
  /** Non-admin layout contract (cards / queue / recent). Absent for ORG_ADMIN. */
  profile?: DashboardNonAdminProfile;
}

/** Response of the admin-only Organization Analysis endpoint. */
export interface DashboardAnalysisResponse {
  scope: DashboardScopePayload;
  dateRange: { startDate: string; endDate: string };
  projectFilter: DashboardProjectFilterPayload;
  /** Analysis widgets (admin-only), in ANALYSIS_WIDGET_KEYS order. */
  widgets: DashboardWidgetPayload[];
}

/**
 * The exact summary cards an ORG_ADMIN sees on the dashboard (org overview).
 * Curated deliberately: the admin dashboard never receives personal "My work"
 * cards, approval-queue cards or quick-create actions.
 */
export const ORG_ADMIN_SUMMARY_WIDGET_KEYS = [
  "projects_total",
  "projects_active",
  "users_active",
  "requirements_total",
  "actuals_total",
  "actuals_approved_cost",
] as const;

/**
 * Widgets served by the admin-only /dashboard/analysis endpoint. They honour
 * the Organization Analysis filters (project + date range) and are never part
 * of the non-admin dashboard.
 */
export const ANALYSIS_WIDGET_KEYS = [
  "requirements_by_status",
  "planned_vs_actual",
  "regular_vs_adhoc",
  "labour_by_skill",
  "cost_by_project",
  "recent_approved_requirements",
  "recent_approved_actuals",
] as const;

/**
 * Resolve the widget keys a user may receive, in render order.
 * ORG_ADMIN = the curated org-overview summary set (the analysis widgets come
 * from the separate admin-only /dashboard/analysis endpoint). Everyone else is
 * resolved from effective permissions through resolveNonAdminProfile: the
 * count-card keys first, then the one primary queue section, then the one
 * compact recent section (both only when the profile includes them). The
 * `/dashboard/stats` response carries a widget payload for EVERY key in this
 * set; `profile` tells the web which keys are cards vs queue vs recent. A
 * `reports:view` grant alone can never surface Organization Analysis, which
 * remains exclusive to ORG_ADMIN.
 */
export function resolveAuthorizedWidgetKeys(
  permissions: string[],
  isOrgAdmin = false
): string[] {
  if (isOrgAdmin) return [...ORG_ADMIN_SUMMARY_WIDGET_KEYS];
  const profile = resolveNonAdminProfile(permissions);
  const keys = [...profile.cards];
  if (profile.queue) keys.push(profile.queue.key);
  if (profile.recent) keys.push(profile.recent.key);
  return keys;
}

export function resolveAuthorizedActions(
  permissions: string[],
  isOrgAdmin = false
): DashboardActionPayload[] {
  // The System Admin overview never carries quick-create actions; the
  // permission grants still exist but the dashboard deliberately hides them.
  if (isOrgAdmin) return [];
  return DASHBOARD_ACTIONS.filter(
    (a) => permissions.includes(a.permission)
  ).map((a) => ({ key: a.key, label: a.label }));
}
